'use client';

/**
 * ToolTimeoutOffer — first-failure card of the tool-timeout recovery flow
 * (a backend tool call — course search / content lookup — timed out or its
 * service is down; see lib/tool-timeout-view.ts's ToolTimeoutView.OFFER).
 * Distinct from HandoffTimeoutOptions (the live-agent "no guide available"
 * card): there, the two options are peers; here RETRY LEADS as the filled
 * primary because a lookup blip often clears on a second attempt, whereas a
 * live-agent timeout means a human is still on the way regardless of what
 * the user picks. Built fresh on StandardCard rather than reusing that
 * component — only the data layer (contact-form-validation.ts,
 * support-requests.ts) is shared between the two flows.
 *
 * Purely presentational: the container owns the retry call, the timer that
 * fires it, and whichever WS/HTTP mechanism actually retries the tool.
 *
 * Copy review (governing principle: "Sara speaks in the user's world,
 * never ours" — no internal service names, no mechanism nouns as things
 * that happen to the user): this used to take a `toolLabel` prop
 * (backend's ToolTimeoutOfferEvent.tool_label — "course search"/"content
 * lookup") and render it in the eyebrow as `${TOOL_LABEL} DIDN'T ANSWER`.
 * Dropped entirely (decision "(c)" of the three offered — see this task's
 * report for the full reasoning): its sibling card, ToolTimeoutFailed,
 * already ships a fully generic eyebrow ("STILL UNREACHABLE") for the
 * SAME incident one state later, so keeping a per-tool eyebrow here was
 * already inconsistent within this one card family. The disambiguation a
 * per-tool eyebrow would offer is also already covered by Sara's own
 * spoken/text apology immediately above this card (backend's
 * _MCP_TOOL_TIMEOUT_DIRECTIVES, which names the specific thing that
 * failed in natural language), so the card itself doesn't need to repeat
 * it. `tool_label` itself is now unused anywhere in this frontend — see
 * the report for the recommendation to remove it from the wire contract
 * rather than leave it dangling (the `call_id` precedent).
 */
import { RefreshCw, Clock, WifiOff } from 'lucide-react';
import { STANDARD_CARD_TONE, StandardCard } from './primitives/standard-card';

interface ToolTimeoutOfferProps {
  onRetry: () => void;
  onLeaveDetails: () => void;
  /** Backend-computed single source of truth (ToolTimeoutOfferEvent.
   *  retry_available) for whether a retry should be offered at all — do NOT
   *  re-derive a cap from `attempt`. Mirrors ToolTimeoutFailed's identical
   *  prop/default exactly: a restored marker CAN arrive with attempt===1
   *  and retry_available already false (the cap was hit in an earlier
   *  session), so OFFER needs this the same way FAILED already does — hides
   *  "Try again" ENTIRELY rather than disabling it, promoting "Leave my
   *  details" to the sole, full-width action. Defaults to true so every
   *  existing call site that never passed this keeps today's two-action
   *  rendering unchanged. */
  retryAvailable?: boolean;
  /** Session-recovery Finding 5: set when the last "Try again" tap couldn't
   *  even SEND the retry_tool_call frame (the socket wasn't open) —
   *  ChatContext.retryToolCall reports this back rather than letting the
   *  card enter RETRYING for a frame that never left the browser (which
   *  could then never resolve). Rendered as an honest inline notice rather
   *  than silently doing nothing; the button itself stays live so the user
   *  can just tap it again once reconnected. `null`/undefined renders
   *  nothing — the ordinary "these blips usually clear up" footnote shows
   *  instead. */
  error?: string | null;
}

export function ToolTimeoutOffer({
  onRetry,
  onLeaveDetails,
  retryAvailable = true,
  error,
}: ToolTimeoutOfferProps) {
  return (
    <StandardCard
      tone={STANDARD_CARD_TONE.NEUTRAL}
      icon={<WifiOff aria-hidden="true" className="size-[15px]" />}
      eyebrow="COULDN'T CONNECT"
      title="I couldn't pull that up"
      actions={{
        primary: retryAvailable
          ? {
              label: 'Try again',
              icon: <RefreshCw aria-hidden="true" className="size-[13px]" />,
              onClick: onRetry,
            }
          : {
              label: 'Leave my details',
              onClick: onLeaveDetails,
            },
        secondary: retryAvailable
          ? {
              label: 'Leave my details',
              onClick: onLeaveDetails,
            }
          : undefined,
        footnote: error ? (
          <span role="alert" style={{ color: '#b3261e' }}>
            {error}
          </span>
        ) : (
          <>
            <Clock aria-hidden="true" className="size-[13px]" />
            These blips usually clear up in a moment.
          </>
        ),
      }}
    >
      That&apos;s a hiccup on our end, not yours. I can try again — it often
      works on a second go — or take your details and have a live agent follow
      up.
    </StandardCard>
  );
}
